// Admin Optimization Center.
// Picks a client → on-demand triggers AI analysis of recent conversations
// → reviews suggestions (new FAQs, missing qualification fields, etc.)
// → Accept patches the live handyman; Dismiss archives the suggestion.
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Loader2, Wand2, RefreshCw, MessageCircleQuestion, Tag, ClipboardList,
  AlertTriangle, Check, X, Sparkles, ChevronDown, ChevronRight, Inbox, History,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

const TYPE_META = {
  faq: {
    label: "Ny FAQ",
    icon: MessageCircleQuestion,
    accent: "bg-blue-50 text-blue-700 border-blue-200",
  },
  qualification_field: {
    label: "Kvalificeringsfält",
    icon: ClipboardList,
    accent: "bg-violet-50 text-violet-700 border-violet-200",
  },
  service: {
    label: "Ny tjänst",
    icon: Tag,
    accent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  missing_answer: {
    label: "Saknat svar",
    icon: AlertTriangle,
    accent: "bg-amber-50 text-amber-700 border-amber-200",
  },
};

export default function OptimizationCenter() {
  const { getAuthHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/optimization/clients`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error("status " + res.status);
      const data = await res.json();
      setClients(data.clients || []);
    } catch (e) {
      toast.error("Kunde inte läsa klienter");
    } finally {
      setLoadingClients(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadClients(); }, [loadClients]);

  return (
    <div className="space-y-6" data-testid="optimization-center">
      <header className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#255E69] mb-2">
              <Wand2 className="w-3 h-3" /> Optimering
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight">
              Optimeringscentral
            </h1>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
              AI:n läser igenom senaste veckans riktiga konversationer och föreslår förbättringar — nya FAQ:er, kvalificeringsfält ni borde fråga om, eller ämnen boten inte kunde svara på. Acceptera → patchas direkt på klienten.
            </p>
          </div>
          <Button variant="outline" onClick={loadClients} disabled={loadingClients} data-testid="opt-refresh-clients">
            {loadingClients ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Uppdatera lista
          </Button>
        </div>
      </header>

      {/* Client picker */}
      <ClientPicker
        clients={clients}
        loading={loadingClients}
        selected={selectedClient}
        onSelect={setSelectedClient}
      />

      {/* Suggestions for the selected client */}
      {selectedClient ? (
        <SuggestionsForClient
          client={selectedClient}
          onClientCountsChanged={loadClients}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500" data-testid="opt-empty-state">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-medium text-slate-700">Välj en klient ovan</div>
          <div className="text-xs mt-1">Listan är sorterad efter mest aktiva senaste 7 dagarna.</div>
        </div>
      )}
    </div>
  );
}

function ClientPicker({ clients, loading, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return clients;
    return clients.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(t) ||
        (c.industry || "").toLowerCase().includes(t) ||
        (c.city || "").toLowerCase().includes(t)
    );
  }, [clients, q]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="opt-client-picker">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
        data-testid="opt-client-picker-toggle"
      >
        <div className="min-w-0">
          {selected ? (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Vald klient</div>
              <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                {selected.name}
                {selected.pending_suggestions > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
                    {selected.pending_suggestions} pending
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {selected.industry || "—"} · {selected.recent_conversations} konv. senaste 7 dagarna
              </div>
            </>
          ) : (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Välj klient</div>
              <div className="text-sm font-medium text-slate-700">{loading ? "Laddar..." : `${clients.length} aktiva klienter`}</div>
            </>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-slate-200 max-h-96 overflow-y-auto">
          <div className="p-3 sticky top-0 bg-white border-b border-slate-100">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sök på namn, bransch eller stad..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/30 focus:border-[#255E69]"
              data-testid="opt-client-search"
            />
          </div>
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Inga matchande klienter</div>
          ) : (
            <ul>
              {filtered.map((c) => (
                <li key={c.client_id}>
                  <button
                    onClick={() => { onSelect(c); setOpen(false); setQ(""); }}
                    className={`w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between ${
                      selected?.client_id === c.client_id ? "bg-[#3A8A98]/5" : ""
                    }`}
                    data-testid={`opt-client-${c.client_id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {c.industry || "—"} · {c.recent_conversations} konv.
                      </div>
                    </div>
                    {c.pending_suggestions > 0 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 shrink-0 ml-3">
                        {c.pending_suggestions} pending
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionsForClient({ client, onClientCountsChanged }) {
  const { getAuthHeaders } = useAuth();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/optimization/suggestions/${client.client_id}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error("status " + res.status);
      const data = await res.json();
      setPending(data.pending || []);
      setHistory(data.history || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error("Kunde inte läsa förslag");
    } finally {
      setLoading(false);
    }
  }, [client.client_id, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/optimization/generate/${client.client_id}`,
        { method: "POST", headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `status ${res.status}`);
      const count = (data.suggestions || []).length;
      if (count === 0) {
        toast.info(
          data.evaluated_conversations === 0
            ? "Inga konversationer senaste 7 dagarna att analysera"
            : "Inga nya förslag — boten verkar svara bra på det som kommer in"
        );
      } else {
        toast.success(`${count} nya förslag genererade`);
      }
      await load();
      onClientCountsChanged?.();
    } catch (e) {
      toast.error("AI-anrop misslyckades: " + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const onResolved = () => {
    load();
    onClientCountsChanged?.();
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <div className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
            {client.recent_conversations} konversationer senaste 7 dagarna
          </div>
          <div className="text-xs text-slate-500">
            {counts.accepted || 0} accepterade · {counts.dismissed || 0} avvisade tidigare
          </div>
        </div>
        <Button
          onClick={generate}
          disabled={generating || client.recent_conversations === 0}
          className="bg-[#255E69] hover:bg-[#3A8A98]"
          data-testid="opt-generate-btn"
        >
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {generating ? "AI analyserar…" : "Generera nya förslag"}
        </Button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="opt-loading">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto" />
        </div>
      ) : pending.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500" data-testid="opt-no-pending">
          <Wand2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-medium text-slate-700">Inga öppna förslag</div>
          <div className="text-xs mt-1">Klicka "Generera nya förslag" för att låta AI:n läsa senaste veckans konversationer.</div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="opt-pending-list">
          {pending.map((s) => (
            <SuggestionCard key={s.suggestion_id} suggestion={s} onResolved={onResolved} />
          ))}
        </ul>
      )}

      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200" data-testid="opt-history-section">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-slate-50 text-left"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <History className="w-4 h-4 text-slate-400" />
              Historik ({history.length})
            </div>
            {showHistory ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          </button>
          {showHistory && (
            <ul className="border-t border-slate-100 divide-y divide-slate-100">
              {history.map((s) => (
                <li key={s.suggestion_id} className="px-5 py-3 flex items-start gap-3 text-sm">
                  <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${
                    s.status === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"
                  }`}>
                    {s.status === "accepted" ? "Acc" : "Avv"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800 leading-snug">{renderSummary(s)}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.rationale}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ suggestion, onResolved }) {
  const { getAuthHeaders } = useAuth();
  const meta = TYPE_META[suggestion.type] || { label: suggestion.type, icon: Wand2, accent: "bg-slate-50 text-slate-700 border-slate-200" };
  const Icon = meta.icon;
  const [busy, setBusy] = useState(null); // "accept" | "dismiss"
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState(suggestion.payload || {});

  const accept = async () => {
    setBusy("accept");
    try {
      const body = editing ? { edits } : {};
      const res = await fetch(
        `${API_URL}/api/admin/optimization/suggestions/${suggestion.suggestion_id}/accept`,
        {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "status " + res.status);
      }
      toast.success("Förslaget är aktiverat hos klienten");
      onResolved();
    } catch (e) {
      toast.error("Kunde inte acceptera: " + e.message);
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async () => {
    setBusy("dismiss");
    try {
      const res = await fetch(
        `${API_URL}/api/admin/optimization/suggestions/${suggestion.suggestion_id}/dismiss`,
        { method: "POST", headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error("status " + res.status);
      toast.success("Avvisat");
      onResolved();
    } catch (e) {
      toast.error("Kunde inte avvisa: " + e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <li
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
      data-testid={`opt-suggestion-${suggestion.suggestion_id}`}
    >
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className={`shrink-0 w-10 h-10 rounded-xl border ${meta.accent} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${meta.accent}`}>
                {meta.label}
              </span>
              {suggestion.evidence_count > 0 && (
                <span className="text-[10px] text-slate-500">
                  {suggestion.evidence_count}/{suggestion.evaluated_conversations} konv.
                </span>
              )}
            </div>
            <div className="text-sm text-slate-900 leading-snug font-medium">
              {renderSummary(suggestion)}
            </div>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              {suggestion.rationale}
            </p>
          </div>
        </div>

        {/* Editable payload for FAQ / missing_answer */}
        {editing && (suggestion.type === "faq" || suggestion.type === "missing_answer") && (
          <div className="mt-4 space-y-2.5 pl-13" data-testid="opt-edit-form">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
                {suggestion.type === "missing_answer" ? "Ämne" : "Fråga"}
              </label>
              <input
                value={(suggestion.type === "missing_answer" ? edits.topic : edits.question) || ""}
                onChange={(e) => setEdits({ ...edits, [suggestion.type === "missing_answer" ? "topic" : "question"]: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#255E69] focus:border-[#255E69]"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Svar</label>
              <textarea
                value={(suggestion.type === "missing_answer" ? edits.suggested_answer : edits.answer) || ""}
                onChange={(e) => setEdits({ ...edits, [suggestion.type === "missing_answer" ? "suggested_answer" : "answer"]: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#255E69] focus:border-[#255E69]"
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          <Button
            onClick={accept}
            disabled={!!busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`opt-accept-${suggestion.suggestion_id}`}
          >
            {busy === "accept" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Acceptera
          </Button>
          {(suggestion.type === "faq" || suggestion.type === "missing_answer") && (
            <Button
              variant="outline"
              onClick={() => setEditing((v) => !v)}
              disabled={!!busy}
              data-testid={`opt-edit-${suggestion.suggestion_id}`}
            >
              {editing ? "Avbryt redigering" : "Redigera"}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={dismiss}
            disabled={!!busy}
            className="text-slate-500 hover:text-slate-800 hover:bg-slate-100"
            data-testid={`opt-dismiss-${suggestion.suggestion_id}`}
          >
            {busy === "dismiss" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
            Avvisa
          </Button>
        </div>
      </div>
    </li>
  );
}

function renderSummary(s) {
  const p = s.payload || {};
  if (s.type === "faq") return `"${p.question}"`;
  if (s.type === "qualification_field") return `${p.label || p.field_name} (${p.field_name})`;
  if (s.type === "service") return p.service_name;
  if (s.type === "missing_answer") return `Saknat svar: ${p.topic}`;
  return JSON.stringify(p);
}
